import { Component, type ReactNode } from 'react'

interface Props {
  /** The address: a failed page is forgotten once the reader goes elsewhere. */
  resetKey: string
  children: ReactNode
}

interface State { failed: boolean }

/** What stands between one page's failure and the whole platform.
 *
 *  React removes everything it has drawn when a render or an effect throws and
 *  nothing catches it, which leaves a white window with no way to tell what
 *  happened and no navigation to leave by. Here the sidebar stays and the page
 *  says so. React still logs the error to the console.
 *
 *  A class because React offers no other way to catch one. Reset by comparing
 *  the address rather than keyed on it: a key would remount the page on every
 *  ticked sensor, since the selection lives in the route, and lose its window. */
export class PageBoundary extends Component<Props, State> {
  state: State = { failed: false }

  static getDerivedStateFromError(): State {
    return { failed: true }
  }

  componentDidUpdate(previous: Props) {
    if (this.state.failed && previous.resetKey !== this.props.resetKey) {
      this.setState({ failed: false })
    }
  }

  render() {
    return this.state.failed ? <PageFailed /> : this.props.children
  }
}

export function PageFailed() {
  return (
    <div className="pad">
      <p className="notice notice-warn" role="alert">
        This page could not be drawn. No data was changed. Reload the page, or
        choose another page from the menu.
      </p>
      <button type="button" className="button" onClick={() => window.location.reload()}>Reload</button>
    </div>
  )
}
